import React, { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import isEmpty from 'lodash/isEmpty';
import { colors } from '@commonsku/styles';
import { usePoDSearch } from './usePoDSearch';
import PoDFilterSidebar from './PoDFilterSidebar';
import SearchBoxInput from '../product-search/shared/SearchBoxInput';
import ProductGrid from '../product-search/shared/ProductGrid';
import SearchLayout, { SIDEBAR_MIN_WIDTH } from '../product-search/shared/SearchLayout';
import { useProductActions } from '../product-search/shared/useProductActions';
import { useSearchTracking } from '../product-search/shared/useSearchTracking';
import { SearchProduct } from '../product-search/shared/types';
import ProductChevronPopup from '../product/ProductChevronPopup';
import { useSize } from '../../hooks/useSize';
import { calculateContentWidth } from '../product-suggestion/helpers';

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ErrorMessage = styled.div`
  width: 100%;
  color: ${colors.errors.main};
`;

interface PoDSearchProps {
  order?: Record<string, unknown> | null;
  full_order?: Record<string, unknown> | null;
  loading: string[];
  onAddItem: (item: Record<string, unknown>) => Promise<any>;
  onDeleteItem: (itemId: string) => void;
  suggestion?: string;
  isProductsPage?: boolean;
}

const PoDSearch: React.FC<PoDSearchProps> = ({
  order,
  full_order,
  loading,
  onAddItem,
  onDeleteItem,
  suggestion = '',
  isProductsPage = false,
}) => {
  const [ref, size] = useSize();
  const [selectedItem, setSelectedItem] = useState<Record<string, unknown> | null>(null);

  const {
    products,
    query,
    setQuery,
    minPrice,
    maxPrice,
    setMinPrice,
    setMaxPrice,
    clearFilters,
    isLoading,
    error,
    isLastPage,
    showMore,
  } = usePoDSearch({ initialQuery: suggestion, pageSize: 40 });

  const { isSelected, isItemLoading, handleClickProductAction, trackClickEvent } = useProductActions({
    order,
    fullOrder: isProductsPage ? null : full_order,
    onAddItem,
    onDeleteItem,
    loading,
    isProductsPage,
    eventPrefix: 'pod',
  });

  const trackSearchEvent = useSearchTracking('search_pod_product');

  const handleSearchChange = useCallback((newQuery: string) => {
    setQuery(newQuery);
    trackSearchEvent(newQuery);
  }, [setQuery, trackSearchEvent]);

  const contentWidth = calculateContentWidth(size.clientWidth ?? 0, SIDEBAR_MIN_WIDTH) + 6;
  const convertedItems = useMemo(() => products.map((p) => p._raw), [products]);
  // Identity lookup works because selectedItem is always a member of a
  // products page that is appended to, never rebuilt, while the popup is open.
  const selectedIndex = selectedItem ? convertedItems.indexOf(selectedItem) : -1;

  return (
    <SearchLayout
      containerRef={ref}
      isProductsPage={isProductsPage}
      sidebar={
        <PoDFilterSidebar
          minPrice={minPrice}
          maxPrice={maxPrice}
          onChangeMinPrice={setMinPrice}
          onChangeMaxPrice={setMaxPrice}
          onClearFilters={clearFilters}
        />
      }
      content={
        <ContentColumn>
          <SearchBoxInput
            value={query}
            onChange={handleSearchChange}
            isSearchStalled={isLoading}
            placeholder="Search Print On Demand products"
          />
          {error && (
            <ErrorMessage>
              Unable to load products. Search again or keep scrolling to retry.
            </ErrorMessage>
          )}
          <ProductGrid
            products={products}
            isLastPage={isLastPage}
            onLoadMore={showMore}
            loading={loading}
            isLoading={isLoading}
            isProductsPage={isProductsPage}
            isSelected={(product: SearchProduct) => isSelected(product._raw)}
            onClickProduct={(product: SearchProduct) => {
              setSelectedItem(product._raw);
              trackClickEvent();
            }}
            onClickProductAction={(product: SearchProduct) => {
              if (isItemLoading(product._raw)) return;
              handleClickProductAction(product._raw);
            }}
            contentWidth={contentWidth}
            isItemLoading={(product: SearchProduct) => isItemLoading(product._raw)}
          />
          {!isEmpty(selectedItem) && (
            <ProductChevronPopup
              products={convertedItems}
              productIndex={selectedIndex}
              isSelected={(item: Record<string, unknown>) => {
                if (isProductsPage) return false;
                return isSelected(item);
              }}
              onClose={() => setSelectedItem(null)}
              closeOnClickOutside={true}
              handleLoadNextPage={showMore}
              isEnd={isLastPage && selectedIndex === convertedItems.length - 1}
              onClickProductAction={() => handleClickProductAction(selectedItem!, true)}
              isProductsPage={isProductsPage}
            />
          )}
        </ContentColumn>
      }
    />
  );
};

export default PoDSearch;
